import React, { useEffect, useState } from 'react';

import { ModalBasicType } from '@/types/common';
import ModalComponent from '@/components/common-components/ModalComponent/ModalComponent';

import './_onboardingModal.scss';

import OnboardingModalBody from './OnboardingModalBody';
import OnboardingModalButtons from './OnboardingModalButtons';
import ProgressIndicator from './ProgressIndicator';

export interface OnboardingStep {
  header: string;
  description: string | React.ReactNode;
  image: string;
  isVideo?: boolean;
  videoUrl?: string;
}

interface CustomButtonsProps {
  currentStep: number;
  totalSteps: number;
  handleNextStep: () => void;
  handlePreviousStep: () => void;
}

interface Props extends ModalBasicType {
  showCross?: boolean;
  steps: OnboardingStep[];
  onComplete?: () => void;
  width?: number;
  customButtons?: (props: CustomButtonsProps) => React.ReactNode;
  onVideoProgress?: (progress: {
    played: number;
    playedSeconds: number;
    loaded: number;
    loadedSeconds: number;
  }) => void;
}

const OnboardingModal = ({
  show,
  onClose,
  showCross = false,
  steps,
  onComplete,
  width = 764,
  customButtons,
  onVideoProgress,
}: Props) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  // Preload all images when the modal is shown
  useEffect(() => {
    if (show && !imagesPreloaded) {
      const preloadImages = async () => {
        const imagePromises = steps.map((data) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = data.image;
          });
        });

        try {
          await Promise.all(imagePromises);
          setImagesPreloaded(true);
        } catch (error) {
          console.error('Error preloading images:', error);
        }
      };

      preloadImages();
    }
  }, [show, imagesPreloaded, steps]);

  const currentStepData = steps[currentStep - 1];

  const handleNextStep = () => {
    if (currentStep === steps.length) {
      setCurrentStep(1);
      onComplete?.();
      onClose();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <ModalComponent
      noPadding
      show={show}
      onClose={onClose}
      width={width}
      style={{ borderRadius: '16px' }}
      outsideClickClose={false}
      topCross={showCross}>
      <div className="onboarding-modal-container">
        <ProgressIndicator step={currentStep} totalSteps={steps.length} />
        <OnboardingModalBody
          header={currentStepData.header}
          description={currentStepData.description}
          image={currentStepData.image}
          isVideo={currentStepData.isVideo}
          videoUrl={currentStepData.videoUrl}
          onProgress={onVideoProgress}
        />
        {customButtons ? (
          customButtons({
            currentStep,
            totalSteps: steps.length,
            handleNextStep,
            handlePreviousStep,
          })
        ) : (
          <OnboardingModalButtons
            currentStep={currentStep}
            totalSteps={steps.length}
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
          />
        )}
      </div>
    </ModalComponent>
  );
};

export default OnboardingModal;
